import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, ArrowLeft, Star, Quote } from 'lucide-react';

const TestimonialsPage = () => {
  const navigate = useNavigate();

  const testimonials = [
    {
      id: 1,
      name: "Kanha Tiwari",
      role: "Customer",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop",
      rating: 5,
      text: "My chronic back pain and stress levels have dramatically improved thanks to Divine Quanta's advanced quantum therapy. The best part was that I never had to travel—everything was done remotely, yet powerfully effective.",
      location: "Mumbai"
    },
    {
      id: 2,
      name: "Asutosh Shukla",
      role: "Customer",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
      rating: 5,
      text: "After years of struggling with anxiety and emotional trauma, I finally found peace through Divine Quanta. The quantum therapy and meditation guidance brought a shift in my energy and clarity in my life. I feel lighter, more focused, and finally free.",
      location: "Delhi"
    },
    {
      id: 3,
      name: "Mayank Dehariya",
      role: "Customer",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop",
      rating: 5,
      text: "The emotional healing I received at Divine Quanta went deeper than any therapy I've ever tried. The healing sessions healed pain I had buried for years. Truly transformative experience.",
      location: "Bangalore"
    },
    {
      id: 4,
      name: "Priya Sharma",
      role: "Customer",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop",
      rating: 5,
      text: "The Prana Analysis was incredibly accurate. The aura report revealed things about my energy that I had been feeling but couldn't articulate. The subsequent healing sessions have been life-changing.",
      location: "Pune"
    },
    {
      id: 5,
      name: "Rajesh Kumar",
      role: "Customer",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop",
      rating: 5,
      text: "I was skeptical about quantum healing at first, but the results speak for themselves. My sleep quality has improved dramatically, and I feel more energized throughout the day. The remote sessions are convenient and powerful.",
      location: "Hyderabad"
    },
    {
      id: 6,
      name: "Ananya Patel",
      role: "Customer",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop",
      rating: 5,
      text: "Divine Quanta's approach combines science and spirituality perfectly. The quantum technology analysis gave me concrete insights, while the healing sessions addressed deep emotional wounds. Highly recommended!",
      location: "Ahmedabad"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900 relative overflow-hidden">
      {/* Background quantum image */}
      <div className="absolute inset-0 opacity-10">
        <img 
          src="https://images.pexels.com/photos/5475762/pexels-photo-5475762.jpeg"
          alt="Healing Background"
          className="w-full h-full object-cover"
          style={{mixBlendMode: 'screen'}}
        />
      </div>

      {/* Background quantum particles */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-2 h-2 bg-white rounded-full quantum-float"></div>
        <div className="absolute top-40 right-32 w-3 h-3 bg-purple-300 rounded-full quantum-float" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-32 left-40 w-2 h-2 bg-indigo-300 rounded-full quantum-float" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-purple-500/30 bg-purple-900/40 backdrop-blur-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-8 h-8 text-purple-300" />
              <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Cormorant Garamond, serif'}}>Divine Quanta</h1>
            </div>
            <Button
              data-testid="back-home-btn"
              onClick={() => navigate('/home')}
              variant="ghost"
              className="text-purple-200 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="relative z-10 container mx-auto px-6 py-20">
        {/* Page Title */}
        <div className="text-center mb-16">
          <div className="inline-block bg-purple-500/30 text-purple-200 px-6 py-2 rounded-full text-sm backdrop-blur-sm border border-purple-400/30 mb-6">
            <Star className="w-4 h-4 inline mr-2" />
            Client Testimonials
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Healing Stories from Our Clients
          </h2>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Real experiences from people who have transformed their lives through quantum healing therapy
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {testimonials.map((testimonial) => (
            <Card 
              key={testimonial.id} 
              className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-8 hover:bg-purple-900/50 transition-all"
              data-testid={`testimonial-${testimonial.id}`}
            >
              <div className="flex items-start space-x-4 mb-6">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-purple-400"
                />
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">{testimonial.name}</h3>
                  <p className="text-purple-300 text-sm mb-2">{testimonial.location}</p>
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>
                <Quote className="w-8 h-8 text-purple-400/30" />
              </div>
              
              <p className="text-purple-100 leading-relaxed italic">
                "{testimonial.text}"
              </p>
            </Card>
          ))}
        </div>

        {/* Thank You Message from the Founder */}
        <div className="mb-16" data-testid="founder-thank-you">
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/15 via-amber-500/10 to-orange-500/15 blur-3xl rounded-full"></div>
            <div className="relative bg-gradient-to-br from-purple-900/60 via-indigo-950/70 to-purple-900/60 backdrop-blur-xl border-2 border-yellow-400/40 rounded-3xl p-8 md:p-10 spiritual-glow">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-yellow-400/40 to-amber-500/40 border-2 border-yellow-400/60 flex items-center justify-center">
                  <span className="text-yellow-200 font-bold text-xl md:text-2xl" style={{fontFamily: 'Cormorant Garamond, serif'}}>DQ</span>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-block bg-yellow-500/20 px-3 py-1 rounded-full border border-yellow-400/40 mb-3">
                    <span className="text-yellow-300 font-bold text-xs tracking-widest">A NOTE FROM THE FOUNDER</span>
                  </div>
                  <p className="text-white text-lg md:text-xl leading-relaxed" style={{fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic'}}>
                    &ldquo;<span className="text-yellow-100">Thank you for showing your trust in Divine Quanta.</span> We are truly happy to serve you in your healing journey.&rdquo;
                  </p>
                  <p className="text-yellow-300 font-semibold mt-3" style={{fontFamily: 'Cormorant Garamond, serif'}}>&mdash; Divine Quanta Team</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card className="bg-gradient-to-br from-purple-800/50 to-indigo-900/50 backdrop-blur-lg border border-white/20 p-8 text-center">
            <div className="text-5xl font-bold text-white mb-2">5000+</div>
            <p className="text-purple-200">Happy Clients</p>
          </Card>
          <Card className="bg-gradient-to-br from-purple-800/50 to-indigo-900/50 backdrop-blur-lg border border-white/20 p-8 text-center">
            <div className="text-5xl font-bold text-white mb-2">10+</div>
            <p className="text-purple-200">Years Experience</p>
          </Card>
          <Card className="bg-gradient-to-br from-purple-800/50 to-indigo-900/50 backdrop-blur-lg border border-white/20 p-8 text-center">
            <div className="text-5xl font-bold text-white mb-2">98%</div>
            <p className="text-purple-200">Success Rate</p>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Card className="bg-gradient-to-r from-purple-900/60 via-indigo-900/60 to-purple-900/60 backdrop-blur-xl border border-purple-400/30 rounded-3xl p-16 spiritual-glow">
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Ready to Start Your Healing Journey?
            </h3>
            <p className="text-lg text-purple-200 mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied clients who have transformed their lives through quantum healing
            </p>
            <Button 
              data-testid="cta-book-now-btn"
              onClick={() => navigate('/auth')} 
              className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-600 hover:from-purple-700 hover:via-purple-800 hover:to-indigo-700 text-white px-12 py-6 rounded-full text-lg font-bold spiritual-glow"
            >
              Book Your Session Now
            </Button>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-purple-500/30 mt-32">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Sparkles className="w-6 h-6 text-purple-300" />
              <span className="text-white font-bold" style={{fontFamily: 'Cormorant Garamond, serif'}}>Divine Quanta</span>
            </div>
            <p className="text-purple-300 text-sm">© 2025 Divine Quanta. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TestimonialsPage;
